<script lang="ts" setup>
import { computed } from 'vue'
import type { BaselineServiceExpectation, ServiceDiff } from '@/queries/saved-calls'
import {
  getDiffKindBadgeClass,
  getDiffKindLabel,
  getRunStatusBadgeClass,
} from '@/views/builder/utils/runFormatters'

// Two callers, one table:
//  - 'expected': a baseline's stored expectations (no run to compare against yet) — actual columns hidden.
//  - 'diff':     a run validated against its baseline — expected vs actual side by side, coloured by kind.
const props = defineProps<{
  mode: 'expected' | 'diff'
  expectations?: BaselineServiceExpectation[]
  diffs?: ServiceDiff[]
}>()

// A single normalised row so the template never branches on mode. In 'expected' mode the actual
// fields and kind are null and their columns aren't rendered.
interface Row {
  serviceId: string
  serviceName: string
  expectedStatus: string | null
  actualStatus: string | null
  expectedSuccess: number
  actualSuccess: number | null
  expectedWarning: number
  actualWarning: number | null
  expectedError: number
  actualError: number | null
  kind: string | null
  detail: string | null
}

const rows = computed<Row[]>(() => {
  if (props.mode === 'expected') {
    return (props.expectations ?? []).map((e) => ({
      serviceId: e.serviceId,
      serviceName: e.serviceName,
      expectedStatus: e.expectedStatus,
      actualStatus: null,
      expectedSuccess: e.expectedSuccess,
      actualSuccess: null,
      expectedWarning: e.expectedWarning,
      actualWarning: null,
      expectedError: e.expectedError,
      actualError: null,
      kind: null,
      detail: null,
    }))
  }
  return (props.diffs ?? []).map((d) => ({
    serviceId: d.serviceId,
    serviceName: d.serviceName,
    expectedStatus: d.expectedStatus,
    actualStatus: d.actualStatus,
    expectedSuccess: d.expectedSuccessCount,
    actualSuccess: d.actualSuccessCount,
    expectedWarning: d.expectedWarningCount,
    actualWarning: d.actualWarningCount,
    expectedError: d.expectedErrorCount,
    actualError: d.actualErrorCount,
    kind: d.kind,
    detail: d.detail,
  }))
})

const showActual = computed(() => props.mode === 'diff')

// A blank status cell means the service was in the baseline but absent from the run (or vice versa);
// render a dash rather than an empty badge.
function statusOrDash(status: string | null): string {
  return status ?? '—'
}
</script>

<template>
  <div v-if="rows.length === 0" class="text-muted small">No services in this baseline.</div>
  <div v-else class="table-responsive">
    <table class="table table-sm align-middle mb-0 small">
      <thead>
        <tr>
          <th>Service</th>
          <th v-if="showActual">Kind</th>
          <th>{{ showActual ? 'Expected' : 'Status' }}</th>
          <th v-if="showActual">Actual</th>
          <th class="text-end">Success</th>
          <th class="text-end">Warning</th>
          <th class="text-end">Error</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.serviceId">
          <td>
            <div class="fw-medium">{{ row.serviceName }}</div>
            <div v-if="row.detail" class="text-muted">{{ row.detail }}</div>
          </td>
          <td v-if="showActual">
            <span class="badge" :class="getDiffKindBadgeClass(row.kind ?? undefined)">
              {{ getDiffKindLabel(row.kind ?? undefined) }}
            </span>
          </td>
          <td>
            <span class="badge" :class="getRunStatusBadgeClass(row.expectedStatus ?? undefined)">
              {{ statusOrDash(row.expectedStatus) }}
            </span>
          </td>
          <td v-if="showActual">
            <span class="badge" :class="getRunStatusBadgeClass(row.actualStatus ?? undefined)">
              {{ statusOrDash(row.actualStatus) }}
            </span>
          </td>
          <td class="text-end">
            <span>{{ row.expectedSuccess }}</span>
            <span v-if="showActual" class="text-muted"> → {{ row.actualSuccess }}</span>
          </td>
          <td class="text-end">
            <span>{{ row.expectedWarning }}</span>
            <span v-if="showActual" class="text-muted"> → {{ row.actualWarning }}</span>
          </td>
          <td class="text-end">
            <span>{{ row.expectedError }}</span>
            <span v-if="showActual" class="text-muted"> → {{ row.actualError }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
